import type { ShareableField, ShareableFieldValue } from '@amzn/journeys-common';
import { sortBy, uniqBy } from 'es-toolkit/compat';
import type { ShareableFieldConfigBase } from 'features/traffic/campaigns/campaign-configs/types';
import { useFlavor } from 'features/traffic/campaigns/useFlavor';
import { useSpyOnMultipleFieldValues } from 'hooks/form-spy/useSpyOnMultipleFieldValues';
import pluralize from 'pluralize';
import { useMemo } from 'react';
import { useFormState } from 'react-final-form';
import { useThrottle } from 'react-use';
import { getFlavorIndexFromFinalFormName, removeFlavorsAndIndexFromFieldPath, type TrafficCampaignFormValues } from '../../generic.util';
import type { FullFormFriendlyPath, ShareOption } from '../shared-variables.types';
import { useSharedVarsContext } from '../shared-variables-context';

const isShareableField = (field: unknown | ShareableField): field is ShareableField => {
  if (!field) return false;
  if ((field as ShareableField).sharedVarKey === undefined) return false;
  if ((field as ShareableField).value === undefined) return false;
  return true;
};

/**
 * Takes two field paths and returns a number representing how similar they are
 * - This is used for sorting share options
 * - This will become more important for other campaign types or for Herotator if we decide to support sharing `linkTypeAndLinkTypeValues`
 * or `width` values between different area indices. Ex: `flavors[0].content.areas.0.width` sharing with `flavors[1].content.areas.3.width`
 * - returns 15 if the paths are identical (looking at the same field)
 * - returns 10 if the paths are similar (looking at the same field but different flavors)
 * - returns 5 if the paths are not similar (looking at different fields)
 */
const getPathSimilarityScore = (pathA: FullFormFriendlyPath, pathB: FullFormFriendlyPath) => {
  if (pathA === pathB) return 15;
  const aContentPath = removeFlavorsAndIndexFromFieldPath(pathA);
  const bContentpath = removeFlavorsAndIndexFromFieldPath(pathB);
  if (aContentPath === bContentpath) return 10;
  return 5;
};

/**
 * Takes an array of area labels and combines them into a string
 * - `Intl.ListFormat.format(['foo', 'bar', 'baz']) => "foo, bar, and baz"`
 * - `Intl.ListFormat.format(['foo', 'bar']) => "foo and bar"`
 * - need to update caniuse to remove @ts-expect-error flag
 */
const formatFlavorLabels = (flavorLabels: string[]) => {
  const formatter = new Intl.ListFormat('en-US', {
    style: 'long',
    type: 'conjunction',
  });
  return formatter.format(flavorLabels);
};

type UseShareOptionsProps<TFieldValue extends ShareableFieldValue, TFlavorContent extends Record<string, unknown>> = {
  name: FullFormFriendlyPath;
  shareableFieldConfig: ShareableFieldConfigBase<TFieldValue, TFlavorContent>;
};

/**
 * Returns share options for a given flavor.
 */
export const useShareOptions = <TFieldValue extends ShareableFieldValue, TFlavorContent extends Record<string, unknown>>({
  name,
  shareableFieldConfig,
}: UseShareOptionsProps<TFieldValue, TFlavorContent>): ShareOption[] => {
  const { flavorIndexToDisplayNameMap, getSharedVar, usedByPathToSharedVarKeyMap, sharedVarsMap } = useSharedVarsContext();
  const formState = useFormState<TrafficCampaignFormValues>();
  const flavorIndex = useMemo(
    () => getFlavorIndexFromFinalFormName(name),
    [
      name,
    ],
  );
  const flavor = useFlavor(flavorIndex);

  const otherFieldPaths = useMemo(() => {
    // Get all field paths for the other flavors
    const otherFlavorFieldPaths = formState.values.flavors.reduce((acc, flav, flavIndex) => {
      // Dont show sharing option for inactive flavors
      if (!flav.active) return acc;
      // If we're looking at the same flavor...
      if (flavIndex === flavorIndex) return acc;
      // Points to a ShareableField, not the underlying `value`
      const otherFlavorFieldPaths = shareableFieldConfig.getShareableFieldPaths({
        fieldPath: name,
        otherFlavor: flav,
        otherFlavorIndex: flavIndex,
        referenceFlavor: flavor,
        referenceFlavorIndex: flavorIndex,
        sharedVarsMap,
      });
      return [
        // biome-ignore lint/performance/noAccumulatingSpread: legacy code
        ...acc,
        ...otherFlavorFieldPaths,
      ];
    }, [] as string[]);

    // Add this field's field path to the list of fields to track
    // If this field uses a shared var, we need to respond to updates to that shared var value
    // If it does not, this will be ignored
    otherFlavorFieldPaths.push(name);

    // If any of the otherFlavorFieldPaths paths use a shared var, we need to track that shared var value
    const sharedVarsToTrack: string[] = otherFlavorFieldPaths.reduce((acc, fp) => {
      const sharedVarKey = usedByPathToSharedVarKeyMap[fp];
      return sharedVarKey
        ? [
            // biome-ignore lint/performance/noAccumulatingSpread: legacy code
            ...acc,
            `varSharing.sharedVarsMap.${sharedVarKey}`,
          ]
        : acc;
    }, [] as string[]);
    return [
      ...otherFlavorFieldPaths,
      ...sharedVarsToTrack,
    ];
  }, [
    formState.values.flavors,
    name,
    flavorIndex,
    shareableFieldConfig,
    flavor,
    sharedVarsMap,
    usedByPathToSharedVarKeyMap,
  ]);

  const potentialMatchingFields = useSpyOnMultipleFieldValues({
    fieldPaths: otherFieldPaths,
  });

  // We don't need to re-calculate share options all that often
  const throttledMatchingFields = useThrottle(potentialMatchingFields, 500);

  const shareOptions = useMemo(() => {
    const newShareOptions: ShareOption[] = [];

    Object.entries(throttledMatchingFields).forEach(([fieldPath, shareableField]) => {
      if (!isShareableField(shareableField)) {
        return;
      }

      const { sharedVarKey, value } = shareableField;

      if (sharedVarKey) {
        const sharedVar = getSharedVar(sharedVarKey);
        if (!sharedVar) return;
        const flavorLabels: string[] = sharedVar.usedByPaths.map((usedByPath) => {
          const flavIndex = getFlavorIndexFromFinalFormName(usedByPath);
          return flavorIndexToDisplayNameMap[flavIndex];
        });
        const sortScore: number = sharedVar.usedByPaths.reduce((max, usedByPath) => {
          const similarityScore = getPathSimilarityScore(name as FullFormFriendlyPath, usedByPath as FullFormFriendlyPath);
          if (similarityScore > max) return similarityScore;
          return max;
        }, 0);
        const flavorLabelsCombined = formatFlavorLabels(flavorLabels);
        const originLabel = `Used by ${pluralize('variation', flavorLabels.length, true)}: ${flavorLabelsCombined}`;
        newShareOptions.push({
          formattedValue: shareableFieldConfig.formatValue(sharedVar.value as TFieldValue),
          isExistingSharedVar: true,
          optionId: `shared-var-${sharedVarKey}`,
          originLabel,
          sharedVarKey,
          sortScore,
          value: sharedVar.value,
        });
      } else {
        if (!shareableFieldConfig.valueFilterFn(value)) return;
        // Looking at the field that we're calculating options for
        // We don't want to display a separate share option for it
        if (fieldPath === name) return;
        const otherFlavorIndex = getFlavorIndexFromFinalFormName(fieldPath);
        newShareOptions.push({
          fieldPath,
          formattedValue: shareableFieldConfig.formatValue(value),
          isExistingSharedVar: false,
          optionId: `field-option-${fieldPath}`,
          originLabel: flavorIndexToDisplayNameMap[otherFlavorIndex],
          sortScore: getPathSimilarityScore(name as FullFormFriendlyPath, fieldPath as FullFormFriendlyPath),
          value,
        });
      }
    });

    // Show highest sort-score first
    return sortBy(
      uniqBy(newShareOptions, (option) => option.optionId),
      ({ sortScore }) => sortScore * -1,
    );
  }, [
    shareableFieldConfig,
    flavorIndexToDisplayNameMap,
    getSharedVar,
    name,
    throttledMatchingFields,
  ]);

  return shareOptions;
};
